/* eslint-disable lingui/no-unlocalized-strings, lingui/text-restrictions -- Approved First Round plan copy. */
import "./plan-dialog.css";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowRight, Check, Loader2, X } from "lucide-react";
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { track } from "@/client/analytics/mixpanel";
import { trackEvent } from "@/client/analytics/track";
import { toast } from "@/client/hooks/use-toast";
import { SUPPORT_EMAIL } from "@/client/pages/first-round/content";
import {
  codeOffer,
  type FirstRoundCodeCheck,
  type FirstRoundMe,
  type FirstRoundPlanCard,
  formatPlanPrice,
  planErrorMessage,
  useChooseFree,
  useCodeCheck,
  useFirstRoundMe,
  useFirstRoundPlans,
  useRedeemCode,
  useStartCheckout,
} from "@/client/services/pricing/first-round";
import { useAuthStore } from "@/client/stores/auth";
import { usePaymentStore } from "@/client/stores/payment";
import { firstRoundLegalUrl } from "@/client/utils/firstRoundAppUrl";

import {
  type CardAction,
  cardAction,
  ENTERPRISE_CARD,
  PLAN_FOOTNOTES,
  planLines,
} from "./plan-cards";
import { planDialogHeading } from "./plan-reasons";
import { PlanOfferSpacer, PlanPrice } from "./PlanPrice";
import { passWelcome } from "./welcome";

const PRACTICE_PATH = "/dashboard/practice-interview";

/** A checked code, in one line under the field. */
const describeCode = (check: FirstRoundCodeCheck) => {
  if (check.kind === "pass") return `${check.code} unlocks a free ${check.planName} pass.`;
  const plans = check.prices.map((p) => (p.planKey === "starter" ? "Starter" : "Active"));
  return `${check.code}: ${check.label} ${plans.join(" and ")}, taken off at checkout.`;
};

/**
 * The First Round plan dialog (Free, Starter, Active, Enterprise), the only place theResume shows
 * plans. Opened by limits, Upgrade buttons, Settings, Practice Interview, sign-up links carrying
 * ?plan= / ?code=, and as the one-time plan picker. Mounted once, for signed-in users.
 */
export const PlanDialog = () => {
  const signedIn = useAuthStore((state) => Boolean(state.user));
  return signedIn ? <PlanDialogInner /> : null;
};

const PlanDialogInner = () => {
  const open = usePaymentStore((state) => state.showPaymentDialog);
  const type = usePaymentStore((state) => state.dialogType);
  const info = usePaymentStore((state) => state.upgradeInfo);
  const intent = usePaymentStore((state) => state.planIntent);
  const mandatory = usePaymentStore((state) => state.mandatory);
  const reset = usePaymentStore((state) => state.resetPaymentDialog);
  const navigate = useNavigate();

  const plans = useFirstRoundPlans();
  const { data: me } = useFirstRoundMe();
  const chooseFree = useChooseFree();
  const redeem = useRedeemCode();
  const checkout = useStartCheckout();

  const [code, setCode] = useState("");
  // The code as last applied: the server checks it and the cards show what it does.
  const [applied, setApplied] = useState<string | null>(null);
  const [codeMessage, setCodeMessage] = useState<string | null>(null);
  const [planMessage, setPlanMessage] = useState<{ planId: string; text: string } | null>(null);
  const check = useCodeCheck(applied);
  const working = check.isSuccess ? check.data : undefined;
  const selectedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setCode(intent?.code ?? "");
    setApplied(intent?.code?.trim() || null);
    setCodeMessage(null);
    setPlanMessage(null);
    trackEvent("plan_dialog_opened", { reason: type, mandatory });
    if (intent?.code) trackEvent("coupon_applied", { coupon_code: intent.code.toUpperCase() });
    // Only when it opens: later store changes (a limit hit underneath) shouldn't wipe what's typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The plan picked on firstround.com.au, in view (it can be below the fold on a phone).
  useEffect(() => {
    if (open && plans.data && intent?.plan) {
      selectedRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [open, plans.data, intent?.plan]);

  const heading = planDialogHeading(type, info, me);
  const busy = chooseFree.isPending || redeem.isPending || checkout.isPending;

  const onAction = (card: FirstRoundPlanCard, action: CardAction) => {
    setPlanMessage(null);
    if (action.kind === "free") {
      trackEvent("plan_selected", { plan: "free", location: "plan_dialog" });
      chooseFree.mutate(undefined, { onSuccess: () => reset() });
      return;
    }
    if (action.kind !== "buy" && action.kind !== "upgrade") return;

    trackEvent("plan_selected", { plan: card.key, location: "plan_dialog" });
    trackEvent("checkout_started", {
      item_type: "plan",
      item_id: card.id,
      item_name: action.kind === "upgrade" ? `${card.name} upgrade` : card.name,
      value: action.kind === "upgrade" ? action.amount : card.price,
      currency: card.currency,
      is_upgrade: action.kind === "upgrade",
      coupon_code: applied?.toUpperCase(),
    });
    checkout.mutate(
      // A code the server refused isn't sent; one not yet checked is, and checkout checks it.
      {
        planId: action.planId,
        code: (check.isError && applied === code.trim() ? "" : code.trim()) || undefined,
      },
      {
        onSuccess: ({ sessionUrl }) => window.location.assign(sessionUrl),
        onError: (error) =>
          setPlanMessage({
            planId: card.id,
            text: planErrorMessage(error, "We couldn't start checkout. Please try again."),
          }),
      },
    );
  };

  const onApply = (event: FormEvent) => {
    event.preventDefault();
    const value = code.trim();
    if (!value) return setCodeMessage("Enter your code.");
    setCodeMessage(null);
    setPlanMessage(null);
    if (value.toUpperCase() === applied?.toUpperCase()) void check.refetch();
    else setApplied(value);
  };

  // A free-pass code: one click, so the pass's days start when they choose.
  const activate = (value: string) => {
    setCodeMessage(null);
    redeem.mutate(value, {
      onSuccess: (next: FirstRoundMe) => {
        trackEvent("coupon_redeemed", {
          coupon_code: value.toUpperCase(),
          plan: next.planKey ?? "unknown",
        });
        reset();
        const welcome = passWelcome(next);
        if (welcome) toast({ variant: "success", ...welcome });
        navigate(PRACTICE_PATH);
      },
      onError: (error) => setCodeMessage(planErrorMessage(error, "That code didn't work.")),
    });
  };

  let codeStatus: ReactNode = codeMessage;
  if (!codeStatus && applied) {
    if (check.isFetching) codeStatus = `Checking ${applied.toUpperCase()}…`;
    else if (check.isError) {
      codeStatus = planErrorMessage(check.error, "We couldn't check that code. Try again.");
    } else if (working) codeStatus = describeCode(working);
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (next || mandatory) return;
        // Closed without choosing (choosing resets the dialog without coming through here).
        track("plan_dialog_dismissed", { reason: type });
        reset();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="frp-overlay" />
        <DialogPrimitive.Content
          className="frp frp-content"
          aria-describedby={heading.reason ? "frp-reason" : undefined}
          onEscapeKeyDown={(event) => mandatory && event.preventDefault()}
          onInteractOutside={(event) => mandatory && event.preventDefault()}
        >
          {!mandatory && (
            <DialogPrimitive.Close className="frp-close" aria-label="Close">
              <X size={18} aria-hidden="true" />
            </DialogPrimitive.Close>
          )}

          {/* The heading, with the code beside it: in view before any plan, never below them. */}
          <div className="frp-head">
            <div>
              <p className="frp-eyebrow">First Round plans</p>
              <DialogPrimitive.Title className="frp-title">{heading.title}</DialogPrimitive.Title>
              {heading.reason && (
                <DialogPrimitive.Description id="frp-reason" className="frp-reason">
                  {heading.reason}
                </DialogPrimitive.Description>
              )}
            </div>

            <form className="frp-code" onSubmit={onApply}>
              <label htmlFor="frp-code-input">Have a code?</label>
              <div className="frp-code-row">
                <input
                  id="frp-code-input"
                  value={code}
                  placeholder="Enter it here"
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={64}
                  aria-describedby="frp-code-message"
                  onChange={(event) => {
                    setCode(event.target.value);
                    setCodeMessage(null);
                  }}
                />
                <button type="submit" className="frp-button" disabled={busy || check.isFetching}>
                  {check.isFetching && (
                    <Loader2 className="animate-spin" size={16} aria-hidden="true" />
                  )}
                  Apply
                </button>
              </div>
              <p
                id="frp-code-message"
                className="frp-code-message"
                role="status"
                data-tone={codeMessage || check.isError ? "error" : undefined}
              >
                {codeStatus}
                {working?.kind === "pass" && !codeMessage && !check.isFetching && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="frp-code-activate"
                      disabled={busy}
                      onClick={() => activate(working.code)}
                    >
                      {redeem.isPending && (
                        <Loader2 className="animate-spin" size={14} aria-hidden="true" />
                      )}
                      Activate free {working.planName} pass
                    </button>
                  </>
                )}
              </p>
            </form>
          </div>

          {plans.isLoading && (
            <div className="frp-loading">
              <Loader2 className="animate-spin" size={20} aria-label="Loading plans" />
            </div>
          )}
          {plans.isError && (
            <div className="frp-loading">
              <p>
                We couldn't load the plans.{" "}
                <button type="button" className="underline" onClick={() => void plans.refetch()}>
                  Try again
                </button>
              </p>
            </div>
          )}

          {plans.data && (
            <div className="frp-plans">
              {plans.data.map((card) => {
                const action = cardAction(card, me, formatPlanPrice);
                const isCurrent = action.kind === "current";
                const buying = action.kind === "buy";
                // Only cards they can buy show the code: not their current plan, and not an upgrade
                // (which charges the difference, priced by the server).
                const offer = buying ? codeOffer(working, card.key) : null;
                // A free-pass code for this plan: activate instead of paying.
                const freePass = buying && working?.kind === "pass" && offer !== null;
                // The plan picked on firstround.com.au: ready to pay for, in one click.
                const picked = buying && intent?.plan === card.key;
                const label = freePass
                  ? `Activate free ${card.name} pass`
                  : picked
                    ? `Continue to payment · ${formatPlanPrice(offer?.price ?? card.price, card.currency)}`
                    : action.label;
                return (
                  <article
                    key={card.id}
                    ref={picked ? (node) => void (selectedRef.current = node) : undefined}
                    className="frp-plan"
                    data-featured={card.badge && !me?.pass ? "true" : undefined}
                    data-selected={intent?.plan === card.key && !isCurrent ? "true" : undefined}
                    aria-label={`${card.name} plan`}
                  >
                    <div className="frp-plan-head">
                      <h3>{card.name}</h3>
                      {isCurrent ? (
                        <span className="frp-badge" data-tone="current">
                          Current
                        </span>
                      ) : (
                        card.badge && <span className="frp-badge">{card.badge}</span>
                      )}
                    </div>
                    <PlanPrice
                      card={card}
                      code={offer}
                      classes={{ offer: "frp-offer", price: "frp-price", was: "frp-was" }}
                    />
                    <p className="frp-price-note">
                      {card.price === 0 ? "Free" : "One-off. No subscription."}
                    </p>
                    {card.description && <p className="frp-plan-body">{card.description}</p>}
                    <ul className="frp-checks">
                      {planLines(card).map((line) => (
                        <li key={line}>
                          <Check size={15} aria-hidden="true" /> {line}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="frp-button"
                      data-tone={
                        action.kind === "upgrade" || freePass || picked ? "primary" : undefined
                      }
                      disabled={busy || action.kind === "current" || action.kind === "included"}
                      onClick={() =>
                        freePass && working ? activate(working.code) : onAction(card, action)
                      }
                    >
                      {(chooseFree.isPending && action.kind === "free") ||
                      (checkout.isPending && checkout.variables?.planId === card.id) ? (
                        <Loader2 className="animate-spin" size={16} aria-hidden="true" />
                      ) : null}
                      {label}
                      {(action.kind === "buy" || action.kind === "upgrade") && (
                        <ArrowRight size={16} aria-hidden="true" />
                      )}
                    </button>
                    {planMessage?.planId === card.id ? (
                      <p className="frp-plan-message" role="alert">
                        {planMessage.text}
                      </p>
                    ) : (
                      <p className="frp-footnote">{PLAN_FOOTNOTES[card.key]}</p>
                    )}
                  </article>
                );
              })}

              <article className="frp-plan" aria-label="Enterprise plan">
                <div className="frp-plan-head">
                  <h3>{ENTERPRISE_CARD.name}</h3>
                </div>
                <PlanOfferSpacer className="frp-offer" />
                <p className="frp-price">{ENTERPRISE_CARD.price}</p>
                <p className="frp-price-note">For organisations</p>
                <p className="frp-plan-body">{ENTERPRISE_CARD.description}</p>
                <ul className="frp-checks">
                  {ENTERPRISE_CARD.lines.map((line) => (
                    <li key={line}>
                      <Check size={15} aria-hidden="true" /> {line}
                    </li>
                  ))}
                </ul>
                <a
                  className="frp-button"
                  href={SUPPORT_EMAIL}
                  onClick={() =>
                    trackEvent("plan_selected", { plan: "enterprise", location: "plan_dialog" })
                  }
                >
                  {ENTERPRISE_CARD.cta} <ArrowRight size={16} aria-hidden="true" />
                </a>
                <p className="frp-footnote">{ENTERPRISE_CARD.footnote}</p>
              </article>
            </div>
          )}

          <div className="frp-foot">
            <p className="frp-legal">
              Passes are one-off: no subscription, nothing renews. By choosing a plan you agree to
              the First Round <a href={firstRoundLegalUrl("/terms")}>Terms of Service</a>.
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
